'use client';

// app/global-error.tsx — root error boundary that wraps EVERYTHING,
// including the layout chain itself.
//
// Why this file matters beyond app/error.tsx:
//   - app/error.tsx catches render errors inside the root layout's
//     <body>. If the error happens in the layout itself (or in a
//     provider it mounts above the slot), app/error.tsx never sees
//     it — Next.js falls back to its built-in red error overlay and
//     the event never reaches Sentry.
//   - global-error.tsx provides its own <html>+<body>, so it can
//     render even when the root layout fails. This is the only place
//     Sentry can hook a React-render-error in the layout itself.
//
// Sentry warned about this missing file during the 2026-05-27 build
// pass: "It seems like you don't have a global error handler set up.
// It is recommended that you add a 'global-error.js' file with Sentry
// instrumentation so that React rendering errors are reported." This
// file closes that gap.
//
// Like app/error.tsx, this sits OUTSIDE of app/[locale]/, so
// next-intl's useTranslations is not available. We render the
// fallback in all six supported languages in parallel so any visitor
// sees readable text — matches the pattern in app/not-found.tsx and
// app/error.tsx.

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

const GLOBAL_ERROR_COPIES: Array<{ lang: string; title: string; retry: string }> = [
  { lang: 'en', title: 'Something went wrong', retry: 'Try again' },
  { lang: 'de', title: 'Etwas ist schiefgelaufen', retry: 'Erneut versuchen' },
  { lang: 'es', title: 'Algo salió mal', retry: 'Reintentar' },
  { lang: 'fr', title: 'Une erreur est survenue', retry: 'Réessayer' },
  { lang: 'it', title: 'Qualcosa è andato storto', retry: 'Riprova' },
  { lang: 'ja', title: 'エラーが発生しました', retry: '再試行' },
];

export default function GlobalError({ error, reset }: { error: Error; reset: () => void }) {
  useEffect(() => {
    // Capture happens regardless of cookie-consent — global render
    // errors are operational telemetry, not analytics. The SDK
    // itself stays no-op when NEXT_PUBLIC_SENTRY_DSN is unset.
    Sentry.captureException(error);
  }, [error]);

  return (
    <html>
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2rem',
          padding: '3rem 1.5rem',
          background: '#0a0a0a',
          color: '#f5f5f5',
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        {error?.message ? (
          <p style={{ maxWidth: '36rem', textAlign: 'center', fontSize: '0.875rem', color: '#a3a3a3' }}>
            {error.message}
          </p>
        ) : null}
        <ul
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            textAlign: 'center',
            padding: 0,
            listStyle: 'none',
            margin: 0,
          }}
        >
          {GLOBAL_ERROR_COPIES.map((copy) => (
            <li
              key={copy.lang}
              lang={copy.lang}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
            >
              <h1 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>{copy.title}</h1>
              <button
                type="button"
                onClick={reset}
                style={{
                  borderRadius: '9999px',
                  border: '1px solid #525252',
                  background: 'transparent',
                  color: 'inherit',
                  padding: '0.5rem 1.25rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                }}
              >
                {copy.retry}
              </button>
            </li>
          ))}
        </ul>
      </body>
    </html>
  );
}
